"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment-timezone";
import { SearchAutocomplete } from "@/components/common/sidebar/SearchAutocomplete";
import styles from "./timeConvertForm.module.css";

const DEFAULT_FROM_TZ = "Europe/Vienna";
const DEFAULT_TO_TZ = "America/Toronto";
const TIME_FORMAT = "hh:mm A, DD-MM-YYYY";
const REFRESH_INTERVAL_MS = 60_000; // 1 minute

type TimezoneOption = { id: string; name: string; timeZone: string };

function TimeConvertForm() {
  const [selectedFromTz, setSelectedFromTz] = useState<string>(DEFAULT_FROM_TZ);
  const [selectedToTz, setSelectedToTz] = useState<string>(DEFAULT_TO_TZ);
  const [now, setNow] = useState(() => new Date());

  // Flatten: one option per timezone so e.g. USA has America/New_York, America/Los_Angeles, etc.
  const timezoneOptions = useMemo(() => {
    const ct = require("countries-and-timezones");
    const countries = ct.getAllCountries();
    const list: TimezoneOption[] = [];
    Object.values(countries as Record<string, { name: string; timezones: string[] }>).forEach(
      (country: any) => {
        const timezones = country?.timezones ?? [];
        timezones.forEach((tz: string) => {
          const label =
            timezones.length > 1 ? `${country.name} (${tz})` : `${country.name} - ${tz}`;
          list.push({ id: `${country.name}-${tz}`, name: label, timeZone: tz });
        });
      }
    );
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const formattedFrom = useMemo(
    () => moment.tz(now, selectedFromTz).format(TIME_FORMAT),
    [now, selectedFromTz]
  );
  const formattedTo = useMemo(
    () => moment.tz(now, selectedToTz).format(TIME_FORMAT),
    [now, selectedToTz]
  );

  // Initial mount: ensure we have valid timezones
  useEffect(() => {
    setSelectedFromTz(DEFAULT_FROM_TZ);
    setSelectedToTz(DEFAULT_TO_TZ);
    setNow(new Date());
  }, []);

  // Live refresh every minute
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleSelectFrom = useCallback((item: TimezoneOption) => {
    setSelectedFromTz(item.timeZone);
  }, []);

  const handleSelectTo = useCallback((item: TimezoneOption) => {
    setSelectedToTz(item.timeZone);
  }, []);

  const formatResult = useCallback(
    (item: TimezoneOption) => (
      <span style={{ display: "block", textAlign: "left" }}>
        {item.name}
      </span>
    ),
    []
  );

  return (
    <div className={styles.wrapper}>
      <div className={`lenghth-box-design ${styles.card}`}>
        <div className="two-col-grid">
          {/* From timezone */}
          <div className="two-col-grid-items">
            <label className={styles.label}>From</label>
            <div className={styles.timeDisplay} aria-live="polite">
              {formattedFrom}
            </div>
            <div className={styles.timezoneName}>{selectedFromTz}</div>
            <div className={`${styles.searchWrap} all-input-design-alignment`}>
              <SearchAutocomplete<TimezoneOption>
                placeholder="Search country or timezone (e.g. Europe/Vienna)"
                items={timezoneOptions}
                onSelect={handleSelectFrom}
                onSearch={() => {}}
                formatResult={formatResult}
                maxResults={50}
              />
            </div>
          </div>

          {/* To timezone */}
          <div className="two-col-grid-items">
            <label className={styles.label}>To</label>
            <div className={styles.timeDisplay} aria-live="polite">
              {formattedTo}
            </div>
            <div className={styles.timezoneName}>{selectedToTz}</div>
            <div className={`${styles.searchWrap} all-input-design-alignment`}>
              <SearchAutocomplete<TimezoneOption>
                placeholder="Search country or timezone (e.g. America/Toronto)"
                items={timezoneOptions}
                onSelect={handleSelectTo}
                onSearch={() => {}}
                formatResult={formatResult}
                maxResults={50}
              />
            </div>
          </div>
        </div>
      </div>
      <p className={styles.instruction}>
        Select a country or timezone to see the current time. Times update automatically every minute.
      </p>
    </div>
  );
}

export default TimeConvertForm;
